import { useState, useRef } from "react";
import { Box, ClickAwayListener, styled } from "@mui/material";
import {
  ExpandMore as ChevronIcon,
  PeopleAlt as PeopleIcon,
  CardMembership as PlanIcon,
} from "@mui/icons-material";
import { subscriptionPlans } from "../../enums/SubscriptionPlans";
import { t } from "./coach/coachTokens";

// Pill-shaped dropdown trigger — matches the coach ClientsTabFilters aesthetic.
const PillButton = styled("button")(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 11px",
  borderRadius: 20,
  border: `1px solid ${t.border}`,
  background: t.white,
  color: t.t2,
  fontSize: 11.5,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all .13s",
  height: 28,
  whiteSpace: "nowrap",
  "&:hover": { borderColor: t.t3, color: t.text },
}));

const OptionRow = styled(Box, {
  shouldForwardProp: (p) => p !== "selected",
})(({ selected }) => ({
  padding: "8px 14px",
  fontSize: 12.5,
  cursor: "pointer",
  color: selected ? t.greenText : t.text,
  background: selected ? t.greenLight : "transparent",
  fontWeight: selected ? 600 : 400,
  transition: "background .1s",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  gap: 6,
  "&:hover": { background: selected ? t.greenLight : t.bg },
}));

const Dot = ({ color }) => (
  <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
);

const PillDropdown = ({ icon, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const current = options.find((o) => o.value === value) || options[0];

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ position: "relative" }} ref={anchorRef}>
        <PillButton onClick={() => setOpen((o) => !o)} type="button">
          {icon}
          {current.color && <Dot color={current.color} />}
          <span>{current.label}</span>
          <ChevronIcon sx={{ fontSize: 14, transition: "transform .15s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }} />
        </PillButton>
        {open && (
          <Box
            sx={{
              position: "absolute",
              top: "calc(100% + 4px)",
              insetInlineEnd: 0,
              background: t.white,
              border: `1px solid ${t.border}`,
              borderRadius: t.rs,
              boxShadow: t.sh,
              minWidth: 180,
              maxHeight: 280,
              overflowY: "auto",
              zIndex: 1200,
            }}
          >
            {options.map((opt) => (
              <OptionRow
                key={opt.value ?? "all"}
                selected={opt.value === value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
              >
                {opt.color && <Dot color={opt.color} />}
                <span>{opt.label}</span>
              </OptionRow>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

// `null` = "all" for each filter.
const STATUS_OPTS = [
  { value: null, label: "כל הסטטוסים", color: null },
  { value: "active", label: "פעיל", color: t.green },
  { value: "inactive", label: "לא פעיל", color: t.t3 },
];

const PLAN_OPTS = [
  { value: null, label: "כל המסלולים" },
  ...subscriptionPlans.map((p) => ({ value: p.value, label: p.name })),
];

const CompaniesTableFilters = ({ status, onStatus, plan, onPlan }) => (
  <>
    <PillDropdown icon={<PeopleIcon sx={{ fontSize: 12 }} />} value={status} options={STATUS_OPTS} onChange={onStatus} />
    <PillDropdown icon={<PlanIcon sx={{ fontSize: 12 }} />} value={plan} options={PLAN_OPTS} onChange={onPlan} />
  </>
);

export default CompaniesTableFilters;
